import React, { useMemo } from 'react';
import { gql } from '@soundxyz/gql-string';
import { FilteredActivityFeedTypes } from '@components/Feed/store';
import { GridCarousel } from '@components/main-components/carousel/GridCarousel';
import { useAuth } from '@context/auth/base';
import { useMediaQuery } from '@context/mediaQuery';
import { TrendingPostsCarouselDocument, useQuery } from '@graphql/reactQuery';
import { Heading } from './Heading';
import { TrendingPost } from './TrendingPost';
import { TrendingPostSkeleton } from './TrendingPostSkeleton';
import { customArrowClassName } from './shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  query TrendingPostsCarousel(
    $userId: UUID
    $pagination: CursorConnectionArgs!
    $activityFeedFilter: ActivityFeedFilterArgs
    $activityFeedGroupFilter: ActivityFeedGroupFilterArgs
  ) {
    activityFeed(filter: $activityFeedFilter) {
      id
      activityFeedGroups: groups(pagination: $pagination, filter: $activityFeedGroupFilter) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            latestActivityOccurenceAt
            rankingScore
            information {
              __typename
              ... on UserCollectedManySongsAggregate {
                ...UserCollectedManySongsActivity
                post {
                  id
                  webappUri
                  ...FeedPostActionsInfo
                }
              }

              ... on UserCreatedPostAggregate {
                ...FeedActivityTypePost
              }

              ... on SongCollectedByManyAggregate {
                ...ReleaseCollectedByManyActivity
              }

              ... on ReleaseDroppedAggregate {
                release {
                  id
                  ...ArtistDropRelease
                }
                post {
                  id
                  ...FeedPostActionsInfo
                }
              }
            }
          }
        }
      }
    }
  }
`);

const skeletonSlides = Array.from({ length: 5 }).map((_, i) => ({
  id: i.toString(),
  slide: <TrendingPostSkeleton key={`trending-post-skeleton-${i}`} />,
}));

export const TrendingPosts = () => {
  const { authUser } = useAuth();
  const { data, isLoading } = useQuery(TrendingPostsCarouselDocument, {
    variables: {
      activityFeedFilter: { activityFeedType: FilteredActivityFeedTypes.Global },
      activityFeedGroupFilter: { types: ['POSTS'] },
      userId: authUser?.id,
      pagination: {
        first: 10,
      },
    },
    staleTime: 0,
  });
  const { tablet } = useMediaQuery();

  const posts = useMemo(
    () => data?.data.activityFeed?.activityFeedGroups.edges ?? [],
    [data?.data.activityFeed?.activityFeedGroups.edges],
  );

  const postSlides = React.useMemo(
    () =>
      posts
        .filter(postItem => postItem?.node.information.__typename === 'UserCreatedPostAggregate')
        .map(postItem => {
          return {
            id: postItem?.node.id ?? '',
            slide: <TrendingPost postFrag={postItem?.node} />,
          };
        })
        .filter(item => item.slide !== null),
    [posts],
  );

  const content = useMemo(() => {
    const hasEmptySlides = !posts || posts.length === 0;

    if (isLoading)
      return (
        <GridCarousel
          slides={skeletonSlides}
          onEndReached={null}
          onInternalActiveIndexChange={null}
          columnAmount={null}
          hideArrows={!tablet}
          externalActiveIndex={null}
          spacing={null}
          alignSlidesLeft={false}
          ssrSlideWidth={undefined}
          columnType="extra-large"
          arrowClassName={customArrowClassName}
        />
      );

    if (hasEmptySlides) {
      return null;
    }

    return (
      <div className="-mt-3 flex flex-col gap-y-4">
        <GridCarousel
          slides={postSlides.filter(item => item !== null)}
          onEndReached={null}
          onInternalActiveIndexChange={null}
          columnAmount={null}
          hideArrows={!tablet}
          externalActiveIndex={null}
          spacing={null}
          alignSlidesLeft={false}
          ssrSlideWidth={undefined}
          columnType="extra-large"
          arrowClassName={customArrowClassName}
        />
      </div>
    );
  }, [isLoading, postSlides, posts, tablet]);

  return (
    <div className="w-full px-4 md:px-6">
      <Heading />
      {content}
    </div>
  );
};
